import React, { useMemo, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { ResourceRequestStatus } from '~/types';
import {
  DragEndIndicator,
  DragStartIndicator,
  TimelineBlock
} from '~/modules/resourcing/common/chart/components';
import { isInChartDisplayDateRange } from '~/modules/resourcing/common/hooks/useClippingDateRangeUtil';
import {
  RESIZE_DIRECTION_ENUM,
  PERIOD_SCALE_ENUM
} from '~/modules/resourcing/common/enums';
import {
  getDayViewRoundedHours,
  getDayViewRoundedPercentage
} from '~/modules/resourcing/common/util/scheduleUtil';
import useStyles from './useStyles';

const ResourceRequestOverlayPeriodBlock = ({
  classes: classesOverride,
  handleRequestPeriodClick,
  startDate,
  index,
  totalHours,
  dynamicPosition,
  isEditable,
  isOverlay,
  isPartialPeriod,
  chartDisplayDateRange,
  scale,
  totalScheduleHours,
  requestPeriods,
  resourceRequest,
  endDate,
  dragState,
  showResizeIndicator = true,
  percentageValue,
  isPSARMP2023Q3BugsEnabled
}) => {
  const { formatMessage } = useIntl();
  const filteredRequestPeriods = (requestPeriods || []).filter(
    period => !period.isOverlay
  );

  const shouldShowLeftBorder =
    filteredRequestPeriods.length &&
    filteredRequestPeriods[0].startDate === startDate &&
    filteredRequestPeriods[0].endDate === endDate;

  const shouldShowRightBorder =
    filteredRequestPeriods.length &&
    filteredRequestPeriods[filteredRequestPeriods.length - 1].startDate ===
      startDate &&
    filteredRequestPeriods[filteredRequestPeriods.length - 1].endDate ===
      endDate;

  const classes = useStyles({
    quantity: resourceRequest.quantity,
    classes: classesOverride
  });

  const periodBlockClasses = useMemo(
    () => ({
      container: classNames(classes.periodBlock, {
        [classes.toBeHired]:
          resourceRequest.requestStatus === ResourceRequestStatus.Tobehired,
        [classes.rejected]:
          resourceRequest.requestStatus === ResourceRequestStatus.Rejected
      })
    }),
    [
      classes.periodBlock,
      classes.toBeHired,
      classes.rejected,
      resourceRequest.requestStatus
    ]
  );

  const dragIndicatorClasses = useMemo(
    () => ({
      resizeHandle: classes.resizeHandle,
      resizeHandleStart: classes.resizeHandleStart,
      resizeHandleEnd: classes.resizeHandleEnd,
      resizeIcon: classes.resizeIcon
    }),
    [
      classes.resizeHandle,
      classes.resizeHandleStart,
      classes.resizeHandleEnd,
      classes.resizeIcon
    ]
  );

  const handleEvent = useCallback(
    event => {
      if (
        event.type === 'click' ||
        event.key === 'Enter' ||
        event.keyCode === 13
      ) {
        handleRequestPeriodClick({
          event,
          startDate,
          totalHours,
          totalScheduleHours,
          requestPeriods,
          resourceRequest,
          endDate
        });
      }
    },
    [
      endDate,
      handleRequestPeriodClick,
      requestPeriods,
      resourceRequest,
      startDate,
      totalHours,
      totalScheduleHours
    ]
  );

  const isInChartRange = isInChartDisplayDateRange({
    chartDisplayDateRange,
    entityDateRange: { startDate, endDate }
  });

  const periodBlockDynamicPosition = useMemo(() => {
    const leftMostPeriod =
      filteredRequestPeriods.length &&
      filteredRequestPeriods[0].startDate === startDate &&
      dragState.currentResizeDirection === RESIZE_DIRECTION_ENUM.START;
    const rightMostPeriod =
      filteredRequestPeriods.length &&
      filteredRequestPeriods[filteredRequestPeriods.length - 1].endDate ===
        endDate &&
      dragState.currentResizeDirection === RESIZE_DIRECTION_ENUM.END;
    const dynamicWidth =
      scale === PERIOD_SCALE_ENUM.WEEKS
        ? dynamicPosition.width - 16
        : dynamicPosition.width;

    return {
      left: leftMostPeriod
        ? dragState.dragDelta + dynamicPosition.left
        : dynamicPosition.left,
      width:
        leftMostPeriod && dragState.dragDelta < 0
          ? dynamicWidth - dragState.dragDelta
          : rightMostPeriod
          ? dynamicWidth + dragState.dragDelta
          : dynamicWidth,
      top:
        index === 0
          ? dynamicPosition.top
          : dynamicPosition.top + 24 * index + (4 * index - 1)
    };
  }, [
    filteredRequestPeriods,
    startDate,
    dragState.currentResizeDirection,
    dragState.dragDelta,
    endDate,
    scale,
    dynamicPosition.width,
    dynamicPosition.left,
    dynamicPosition.top,
    index
  ]);

  const showStartIndicator = Boolean(
    filteredRequestPeriods.length &&
      filteredRequestPeriods[0].startDate === startDate &&
      showResizeIndicator
  );

  const showEndIndicator = Boolean(
    filteredRequestPeriods.length &&
      filteredRequestPeriods[filteredRequestPeriods.length - 1].endDate ===
        endDate &&
      showResizeIndicator
  );

  const percentage = getDayViewRoundedPercentage({
    percentage: percentageValue,
    scale
  });
  const totalAlocatedHours = getDayViewRoundedHours({
    hours: totalHours,
    scale
  });

  return (
    <>
      {index === 0 && isEditable && (
        <div
          data-qe-id={`ResourceRequestOverlayPeriodBlock_${resourceRequest.requestStatus}`}
          aria-label={formatMessage({ id: 'resourceAssignmentDialog.hours' })}
          role="button"
          onClick={handleEvent}
          tabIndex="0"
          onKeyPress={handleEvent}
          className={classNames(classes.overlay, {
            resourceRequestOverlay: !isPartialPeriod,
            [classes.zIndexOverlayContainer]: !isOverlay
          })}
          style={{
            top: dynamicPosition.top,
            left: dynamicPosition.left,
            width: dynamicPosition.width,
            height: `${resourceRequest.quantity * 24 +
              (resourceRequest.quantity - 1) * 4}px`
          }}
        />
      )}
      {isInChartRange && !isOverlay && (
        <div
          className={classNames(classes.root, {
            [classes.cursorChange]: isEditable
          })}
          style={periodBlockDynamicPosition}
        >
          <div
            className={classNames(classes.container, {
              [classes.leftBorder]: shouldShowLeftBorder,
              [classes.rightBorder]: shouldShowRightBorder
            })}
            aria-hidden="true"
          >
            <TimelineBlock
              dataQeId="periodOverlayTimelineBlock"
              hours={totalAlocatedHours}
              showPercentage={totalScheduleHours > 0}
              percentage={percentage}
              classes={periodBlockClasses}
            />
          </div>
          {index === 0 && isEditable && (
            <>
              {showStartIndicator && (
                <div
                  className={classNames(classes.resizeContainer, {
                    [classes.zIndex]: isPSARMP2023Q3BugsEnabled
                  })}
                >
                  <DragStartIndicator
                    classes={dragIndicatorClasses}
                    gestureBindEvents={dragState.gestureBindEvents}
                    startDate={dragState.dates.startDate}
                    showTooltip
                  />
                </div>
              )}
              {showEndIndicator && (
                <div
                  className={classNames(
                    classes.resizeContainer,
                    classes.resizeHandleEnd,
                    { [classes.zIndex]: isPSARMP2023Q3BugsEnabled }
                  )}
                >
                  <DragEndIndicator
                    classes={dragIndicatorClasses}
                    gestureBindEvents={dragState.gestureBindEvents}
                    endDate={dragState.dates.endDate}
                    showTooltip
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

ResourceRequestOverlayPeriodBlock.propTypes = {
  classes: PropTypes.object,
  handleRequestPeriodClick: PropTypes.func,
  startDate: PropTypes.object,
  index: PropTypes.number,
  totalHours: PropTypes.number,
  dynamicPosition: PropTypes.object,
  chartDisplayDateRange: PropTypes.object,
  scale: PropTypes.string,
  totalScheduleHours: PropTypes.number,
  isOverlay: PropTypes.bool,
  isPartialPeriod: PropTypes.bool,
  requestPeriods: PropTypes.array,
  isEditable: PropTypes.bool,
  resourceRequest: PropTypes.object,
  endDate: PropTypes.object,
  dragState: PropTypes.object,
  showResizeIndicator: PropTypes.bool,
  percentageValue: PropTypes.number,
  isPSARMP2023Q3BugsEnabled: PropTypes.bool
};

export default ResourceRequestOverlayPeriodBlock;
